import { useEffect, useState } from 'react';
import { invigilationApi, roleLabel } from '../lib/invigilationApi';
import type { MyDutyView } from '../lib/invigilationApi';

/**
 * MY INVIGILATION DUTIES — the other half of the chart, and the half staff actually care about.
 *
 * ⚠ SCOPED BY THE SIGNED-IN USER, SERVER-SIDE. There is no employee id in the request. A duty list
 * that takes one turns into a staff-movements tracker for anybody with a login.
 *
 * ⚠ PUBLISHED ROWS ONLY, enforced in the reader. A draft is a head of school still moving names
 * around; a teacher who reads a draft and arranges childcare around it has been misled by us.
 *
 * ⚠ Renders NOTHING when there are no duties. A permanent "you have no duties" card on every
 * teacher's screen for eleven months of the year is clutter that teaches people to ignore the spot
 * where the real notice will eventually appear.
 */
export function MyDutiesStrip({ days = 30 }: { days?: number }) {
  const [rows, setRows] = useState<MyDutyView[] | null>(null);

  useEffect(() => {
    let alive = true;
    invigilationApi
      .mine(undefined, days)
      .then((r) => alive && setRows(r))
      .catch(() => alive && setRows([]));
    return () => {
      alive = false;
    };
  }, [days]);

  /**
   * ⚠ Array.isArray, NOT `!rows`. `{}` is truthy and has no `.length`, so the old guard passed and
   * `rows.map` threw one line later — reported by the page sweep on this component.
   */
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return (
    <div className="card mydu">
      <div className="mydu-head">
        <h2>Your invigilation duty</h2>
        <span className="mydu-count">{rows.length} in the next {days} days</span>
      </div>
      <ul className="mydu-list">
        {rows.map((r, i) => (
          <li key={i}>
            <div className="mydu-when">
              <strong>{pretty(r.date)}</strong>
              <span>{r.shift}</span>
            </div>
            <div className="mydu-what">
              <span className="mydu-role">{roleLabel(r.role)}</span>
              {r.roomName
                ? <span className="mydu-room">{r.roomName}</span>
                /* ⚠ Roving duty has no room, and a chip on its own reads like a truncated row.
                   Say what it means instead: the person needs to know where to be. */
                : <span className="mydu-paper">No fixed room — report to the examination desk</span>}
              {/* ⚠ WHICH EXAMINATION. Without it a teacher reads "X-B · Mathematics" on four rows
                  in a term and cannot tell the Pre-Board from the Half-Yearly — which is the part
                  they need when they are asked to swap a duty. */}
              {r.examName && <span className="mydu-exam">{r.examName}</span>}
              {(r.classLabel || r.subjectName) && (
                <span className="mydu-paper">
                  {[r.classLabel, r.subjectName].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
            {/* ⚠ The person standing in the room is entitled to know a rule was bent to put them
                there — but the planner writes "this person" for a chart read by a third party, and
                reading that about yourself is jarring. Framed as what it is: a note about you. */}
            {r.compromise && <p className="mydu-note">Note on this assignment: {r.compromise}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** ⚠ Explicit local midnight. A bare `new Date('2026-08-09')` is parsed as UTC. */
function pretty(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}
